/**
 * etapas.ts — Categoria "Etapas" (10 modelos premium)
 *
 * "Como funciona" em passo a passo, montado em seções com camadas com
 * títulos, cartões, números e fotos do acervo compartilhado.
 *
 * RELEASE: v1.0.0 - 2026-09-10 - 10 modelos novos (Studio, motor de modelos — Lote B)
 */

import { registerPatterns } from './patternRegistry';
import type { StudioPatternDefinition } from './types';
import { FOTO, WEB, b } from './_shared';

/** Atalho para o bloco de etapas. */
function etapas(itens: string, disposicao = 'horizontal', titulo = '') {
  return b('page.steps', { title: titulo, items: itens, layout: disposicao });
}

const TRES =
  'Solicite|Preencha o formulário em um minuto.\n' +
  'Receba a proposta|Enviamos no mesmo dia, sem compromisso.\n' +
  'Comece a usar|Implantação assistida pela nossa equipe.';

const QUATRO =
  'Diagnóstico|Entendemos a sua operação e os gargalos.\n' +
  'Plano de ação|Definimos prioridades e prazos juntos.\n' +
  'Implantação|Configuramos e treinamos o time.\n' +
  'Acompanhamento|Reunião mensal de resultados.';

const modelos: StudioPatternDefinition[] = [
  {
    key: 'etapas.tres-horizontal',
    category: 'etapas',
    label: 'Três etapas lado a lado',
    description: 'Passo a passo simples e direto.',
    preview: 'steps',
    outputs: WEB,
    order: 1,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '1080px', motion: 'fade-up' }, [
        b('ui.title', { text: 'Como funciona', size: 'lg', level: 'h2', align: 'center' }),
        etapas(TRES),
      ]),
    ],
  },

  {
    key: 'etapas.quatro-vertical',
    category: 'etapas',
    label: 'Quatro etapas em lista',
    description: 'Etapas uma abaixo da outra, boas para processos longos.',
    preview: 'steps',
    outputs: WEB,
    order: 2,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'left', maxWidth: '820px', backgroundColor: '#f8fafc', motion: 'fade' }, [
        b('ui.title', { text: 'O caminho até o resultado', size: 'lg', level: 'h2' }),
        etapas(QUATRO, 'vertical'),
      ]),
    ],
  },

  {
    key: 'etapas.com-foto',
    category: 'etapas',
    label: 'Etapas ao lado da foto',
    description: 'Foto de apoio à direita e etapas à esquerda.',
    preview: 'split-right',
    outputs: WEB,
    order: 3,
    build: () => [
      b('section.layered', { layout: 'split', paddingY: 'lg', align: 'left', maxWidth: '1120px', motion: 'fade-up' }, [
        b('ui.title', { text: 'Do primeiro contato à entrega', size: 'lg', level: 'h2' }),
        etapas(QUATRO, 'vertical'),
        b('ui.image', { src: FOTO.reuniao, alt: 'Reunião de alinhamento', ratio: 'portrait', radius: 'xl', shadow: 'lg' }),
      ]),
    ],
  },

  {
    key: 'etapas.escuro',
    category: 'etapas',
    label: 'Etapas em fundo escuro',
    description: 'Fundo escuro com brilho e três passos em destaque.',
    preview: 'steps',
    outputs: WEB,
    order: 4,
    build: () => [
      b(
        'section.layered',
        { paddingY: 'xl', align: 'center', maxWidth: '1080px', backgroundGradient: 'mesh-dark', glow: 'center', textColor: '#ffffff', motion: 'fade' },
        [
          b('ui.badge', { text: 'Processo', variant: 'glass', align: 'center' }),
          b('ui.title', { text: 'Três passos e pronto', size: 'lg', level: 'h2', align: 'center', accentColor: '#7dd3fc' }),
          etapas(TRES),
        ],
      ),
    ],
  },

  {
    key: 'etapas.com-cta',
    category: 'etapas',
    label: 'Etapas com convite',
    description: 'Passo a passo seguido de botão para começar.',
    preview: 'steps',
    outputs: WEB,
    order: 5,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '1000px', motion: 'fade-up' }, [
        etapas(TRES, 'horizontal', 'Comece em três passos'),
        b('ui.buttons', { items: 'Quero começar|#formulario|solid', align: 'center', size: 'lg' }),
      ]),
    ],
  },

  {
    key: 'etapas.com-numeros',
    category: 'etapas',
    label: 'Etapas com prazos',
    description: 'Números de prazo reforçam cada fase do processo.',
    preview: 'steps',
    outputs: WEB,
    order: 6,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '1120px', layout: 'bento', pattern: 'grid', motion: 'fade' }, [
        b('ui.title', { text: 'Prazos claros em cada fase', size: 'lg', level: 'h2', align: 'center' }),
        b('ui.stat', { value: '1 dia', label: 'para a proposta', align: 'center' }),
        b('ui.stat', { value: '7 dias', label: 'para a implantação', align: 'center' }),
        etapas(TRES),
      ]),
    ],
  },

  {
    key: 'etapas.cartoes',
    category: 'etapas',
    label: 'Etapas em cartões',
    description: 'Cada passo dentro de um cartão numerado.',
    preview: 'grid-3',
    outputs: WEB,
    order: 7,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'center', maxWidth: '1080px', layout: 'bento', motion: 'fade-up' }, [
        b('ui.card', { icon: '1', title: 'Solicite', text: 'Preencha o formulário em um minuto.', variant: 'solid', radius: 'xl', shadow: 'md' }),
        b('ui.card', { icon: '2', title: 'Receba a proposta', text: 'Enviamos no mesmo dia, sem compromisso.', variant: 'solid', radius: 'xl', shadow: 'md' }),
        b('ui.card', { icon: '3', title: 'Comece a usar', text: 'Implantação assistida pela nossa equipe.', variant: 'solid', radius: 'xl', shadow: 'md' }),
      ]),
    ],
  },

  {
    key: 'etapas.sobre-foto',
    category: 'etapas',
    label: 'Etapas sobre a foto',
    description: 'Foto de fundo escurecida com o passo a passo por cima.',
    preview: 'steps',
    outputs: WEB,
    order: 8,
    build: () => [
      b(
        'section.layered',
        { backgroundImage: FOTO.industria, overlay: 'darker', paddingY: 'xl', align: 'center', textColor: '#ffffff', maxWidth: '1080px', motion: 'fade' },
        [b('ui.title', { text: 'Nosso método na prática', size: 'lg', level: 'h2', align: 'center' }), etapas(TRES)],
      ),
    ],
  },

  {
    key: 'etapas.detalhada',
    category: 'etapas',
    label: 'Processo detalhado',
    description: 'Quatro fases com texto de contexto acima.',
    preview: 'steps',
    outputs: WEB,
    order: 9,
    build: () => [
      b('section.layered', { paddingY: 'lg', align: 'left', maxWidth: '900px', motion: 'fade-up' }, [
        b('ui.badge', { text: 'Metodologia', variant: 'soft' }),
        b('ui.title', { text: 'Um método aplicado em centenas de empresas', size: 'lg', level: 'h2' }),
        b('ui.paragraph', { text: 'Cada fase tem responsável, entregas e prazo combinado desde o início.' }),
        etapas(QUATRO, 'vertical'),
      ]),
    ],
  },

  {
    key: 'etapas.compacta',
    category: 'etapas',
    label: 'Passo a passo compacto',
    description: 'Versão curta para encaixar entre duas seções maiores.',
    preview: 'steps',
    outputs: WEB,
    order: 10,
    build: () => [
      b('section.layered', { paddingY: 'md', align: 'center', maxWidth: '960px', backgroundGradient: 'soft', radius: 'xl', motion: 'fade' }, [
        etapas(TRES),
      ]),
    ],
  },
];

registerPatterns(modelos);
